import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import './Table.css';

const __tableStyleTagId = 'table-widget-dynamic-styles';

const __tableEnsureStyleTag = () => {
  if (typeof document === 'undefined') return null;
  let tag = document.getElementById(__tableStyleTagId);
  if (!tag) {
    tag = document.createElement('style');
    tag.id = __tableStyleTagId;
    document.head.appendChild(tag);
  }
  return tag;
};

const __tableHash = (str) => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h).toString(36);
};

const __tableEnsureRule = (className, cssText) => {
  const tag = __tableEnsureStyleTag();
  if (!tag) return;
  if (tag.textContent.includes(`.${className}{`)) return;
  tag.appendChild(document.createTextNode(`\n.${className}{${cssText}}\n`));
};

const __tableToCssSize = (v) => (typeof v === 'number' ? `${v}px` : (v ?? ''));

const __tableGetDynClass = ({
  width,
  height,
  headerBgColor,
  headerTextColor,
  bodyBgColor,
  bodyTextColor
}) => {
  const payload = JSON.stringify({ width, height, headerBgColor, headerTextColor, bodyBgColor, bodyTextColor });
  const cls = `table-dyn-${__tableHash(payload)}`;

  const w = __tableToCssSize(width) || '100%';
  const h = __tableToCssSize(height) || '100%';

  __tableEnsureRule(
    cls,
    `width:${w};height:${h};` +
      `--table-header-bg:${headerBgColor};` +
      `--table-header-text:${headerTextColor};` +
      `--table-body-bg:${bodyBgColor};` +
      `--table-body-text:${bodyTextColor};`
  );

  return cls;
};

const TableWidget = ({
  title,
  xData = [],
  curves = [],
  draggable = false,
  transparentBackground = false,
  dims = { width: '100%', height: '100%' },
  headerBgColor = 'var(--header-background)',
  headerTextColor = 'var(--header-color)',
  bodyBgColor = 'var(--cell-background)',
  bodyTextColor = 'var(--cell-color)'
}) => {
  const [cols, setCols] = useState([]);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    setCols([
      {
        field: 't',
        headerName: 'Time',
        flex: 1,
        minWidth: 170
      },
      ...curves.map((c) => ({
        field: `${c.pin}_${c.deviceId}`,
        headerName: c.yLabel || c.pin,
        flex: c.flex || 1,
        minWidth: 100
      }))
    ]);

    const newRows = xData.reduce((accumulator, utcString, idx) => {
      const shouldSkip = curves.some((c) => {
        if (!c.customize) return false;
        const rawValue = Array.isArray(c.yData) ? c.yData[idx] : null;
        if (rawValue === null || isNaN(rawValue)) return true;
        return rawValue < c.min || rawValue > c.max;
      });
      if (shouldSkip) {
        return accumulator;
      }

      const localTimeString = new Date(utcString).toLocaleString('en-US', {
        timeZone: 'Asia/Damascus',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });

      const rowObj = { id: idx, t: localTimeString };

      curves.forEach((c) => {
        const rawVal = Array.isArray(c.yData) ? c.yData[idx] : null;
        const fieldKey = `${c.pin}_${c.deviceId}`;
        rowObj[fieldKey] = rawVal === undefined || rawVal === null ? null : rawVal;
      });

      accumulator.push(rowObj);
      return accumulator;
    }, []);

    setRows(newRows);
  }, [xData, curves]);

  const handleDragStart = (e) => {
    const widgetEl = e.target.closest('.widget');
    if (!widgetEl) return;
    const widgetId = widgetEl.id;
    const rect = widgetEl.getBoundingClientRect();
    const offsetX = e.clientX - rect.x;
    const offsetY = e.clientY - rect.y;
    let dragData = `table;${widgetId};${offsetX};${offsetY};`;
    dragData += widgetId.startsWith('base') ? 'add' : 'move';
    e.dataTransfer.setData('text/plain', dragData);
  };

  const setGrabbing = (e, on) => {
    if (!draggable) return;
    const el = e.target.closest('.widget-container');
    if (!el) return;
    el.classList.toggle('is-grabbing', Boolean(on));
  };

  const dynClass = __tableGetDynClass({
    width: dims?.width,
    height: dims?.height,
    headerBgColor,
    headerTextColor,
    bodyBgColor,
    bodyTextColor
  });

  return (
    <div
      className={
        `widget-container table-widget ${dynClass} ` +
        `${draggable ? 'is-draggable' : 'not-draggable'} ` +
        `${transparentBackground ? 'is-transparent' : ''}`
      }
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onMouseDown={(e) => setGrabbing(e, true)}
      onMouseUp={(e) => setGrabbing(e, false)}
      onMouseLeave={(e) => setGrabbing(e, false)}
    >
      {title && (
        <h3 className='table-title'>
          {title}
          {curves.length > 0 ? ` [${curves.map((c) => c.pin).join(',')}]` : ''}
        </h3>
      )}

      <div className="table-widget__grid">
        <DataGrid
          className="table-widget__datagrid"
          rows={rows}
          columns={cols}
          getRowId={(r) => r.id}
          autoHeight={false}
          disableSelectionOnClick
        />
      </div>
    </div>
  );
};

TableWidget.propTypes = {
  title: PropTypes.string,
  xData: PropTypes.arrayOf(PropTypes.string),
  curves: PropTypes.arrayOf(
    PropTypes.shape({
      pin: PropTypes.string.isRequired,
      deviceId: PropTypes.number.isRequired,
      yData: PropTypes.array.isRequired,
      yLabel: PropTypes.string,
      flex: PropTypes.number,
      customize: PropTypes.bool,
      min: PropTypes.number,
      max: PropTypes.number
    })
  ),

  draggable: PropTypes.bool,
  transparentBackground: PropTypes.bool,
  dims: PropTypes.shape({
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  headerBgColor: PropTypes.string,
  headerTextColor: PropTypes.string,
  bodyBgColor: PropTypes.string,
  bodyTextColor: PropTypes.string
};

export default TableWidget;
